import type { StoryIssue } from 'common/building-blocks/link-capability';
import { COMPONENT_DISPLAY_NAME } from 'common/constants';
import type { ComponentTypes } from 'types/story';

function getTypeName(type: ComponentTypes): string {
	return COMPONENT_DISPLAY_NAME[type as keyof typeof COMPONENT_DISPLAY_NAME] ?? type;
}

export function getStoryIssuePresentation(issue: StoryIssue): {
	title: string;
	description: string;
	severityLabel: string;
} {
	const outerType = getTypeName(issue.primaryElementType);
	const related = issue.relatedElements?.[0];
	const relatedType = related ? getTypeName(related.type) : 'Link';
	const severityLabel = issue.severity === 'error' ? 'Needs fixing' : 'Check this';

	switch (issue.code) {
		case 'interactive-descendant-conflict':
			return {
				title: `${relatedType} inside linked ${outerType}`,
				description: `“${related?.name ?? relatedType}” and “${issue.primaryElementName}” both respond to clicks. Move one outside the other, or remove the outer link.`,
				severityLabel,
			};
		case 'nested-inline-anchor':
			return {
				title: `Text link inside linked ${outerType}`,
				description: `The link inside “${related?.name ?? relatedType}” conflicts with “${issue.primaryElementName}”. Remove one of the links.`,
				severityLabel,
			};
		case 'opaque-descendant':
			return {
				title: `${relatedType} inside linked ${outerType}`,
				description: `“${related?.name ?? relatedType}” may contain its own controls. Move it outside “${issue.primaryElementName}”, or remove the outer link.`,
				severityLabel,
			};
		case 'missing-internal-card':
			return {
				title: 'Link destination is missing',
				description: `Choose an existing destination card for “${issue.primaryElementName}”.`,
				severityLabel,
			};
		case 'missing-internal-block':
			return {
				title: 'Scroll target is missing',
				description: `Choose another section on the destination card for “${issue.primaryElementName}”.`,
				severityLabel,
			};
		default:
			return {
				title: 'Story issue',
				description: `Review “${issue.primaryElementName}” before publishing.`,
				severityLabel,
			};
	}
}
